import React, { useEffect, useRef, useState } from 'react'

import { functions, path, useApp } from '@wap-client/core'

import SocialShare from '../social-share'

import { useDeviceType } from '@/hooks/useDeviceType'

import { Container } from '@/components/base/gridview'
import Breadcrumbs from '@/components/base/breadcrumbs'

import { AnimatedHeroProps, AnimatedHeroStyles } from './types'
import SocialMedia from '../social-media'

const AnimatedHero: React.FunctionComponent<AnimatedHeroProps> = ({
  assets = [],
  breadcrumbs,
  backdrop,
  headingsSettings = {},
  headings = [],
}) => {
  const app = useApp()
  const deviceType = useDeviceType()

  const [registers, setRegisters] = useState<string[]>([])

  const headingsRef = useRef<HTMLHeadingElement>(null)
  const backdropRef = useRef<HTMLImageElement>(null)
  const assetsRef = useRef<HTMLImageElement[]>([])

  useEffect(() => {
    document.body.style.paddingTop = '0px'
  }, [])

  useEffect(() => {
    if (headingsRef.current) {
      const handleScroll = () => {
        const scrollTop = window.scrollY

        if (scrollTop) {
          if (headingsRef.current) {
            headingsRef.current.style.position = 'sticky'
          }
        } else {
          if (headingsRef.current) {
            headingsRef.current.style.position = 'absolute'
          }
        }
      }

      window.addEventListener('scroll', handleScroll, false)

      return () => window.removeEventListener('scroll', handleScroll, false)
    }
  }, [])

  useEffect(() => {
    if (backdropRef.current && backdropRef.current.complete && backdrop) {
      if (!registers.includes(backdrop)) {
        handleRegister(backdrop)
      }
    }

    if (assetsRef.current && assetsRef.current.length) {
      for (let index = 0; index < assetsRef.current.length; index++) {
        if (assetsRef.current[index].complete && assets[index]) {
          if (!registers.includes(assets[index].image)) {
            handleRegister(assets[index].image)
          }
        }
      }
    }
  }, [])

  const hasRegistered = (src: string) => {
    return registers.includes(src)
  }

  const hasLoaded = () => {
    if (registers.length === assets.length + 1) return true
  }

  const handleRegister = (src: string) => {
    if (!hasRegistered(src)) {
      setRegisters((registers) => [...registers, src])
    }
  }

  const getAssetStyle = (styles?: AnimatedHeroStyles) => {
    const style: Record<string, any> = {}

    if (styles) {
      Object.keys(styles).forEach((key) => {
        const typedKey = key as keyof AnimatedHeroStyles
        const current = styles[typedKey]

        if (current) {
          if (typeof current === 'object') {
            if (deviceType === 'tablet') {
              style[key] = current['tablet']
            } else if (deviceType === 'mobile') {
              style[key] = current['mobile']
            } else {
              style[key] = current['desktop']
            }
          } else {
            style[key] = current
          }
        }
      })
    }

    return style
  }

  const renderBackdrop = () => {
    if (!backdrop) return null

    return (
      <div
        className={functions.classnames(
          'animated-hero-backdrop',
          hasRegistered(backdrop) && 'animated-hero-backdrop--loaded',
        )}
      >
        <img
          src={path.asset(app.environment, backdrop.src)}
          alt=""
          onLoad={() => handleRegister(backdrop)}
          ref={backdropRef}
        />
      </div>
    )
  }

  const renderAssets = () => {
    if (!assets.length) return null

    return (
      <div className="animated-hero-assets">
        {assets.map((asset, index) => (
          <div
            key={index}
            className={functions.classnames(
              'animated-hero-asset',
              `animated-hero-asset--${asset.animation}`,
              'animated-hero-asset--loaded',
            )}
            style={getAssetStyle(asset.style)}
          >
            <img
              src={path.asset(app.environment, asset.image.src)}
              alt=""
              onLoad={() => handleRegister(asset.image)}
              style={getAssetStyle({
                objectPosition: asset.style?.objectPosition,
              })}
              ref={(e: any) => {
                if (e) assetsRef.current[index] = e
              }}
            />
          </div>
        ))}
      </div>
    )
  }

  const renderHeadings = () => {
    if (!headings.length) return null

    return (
      <div className="animated-hero-headings-container">
        <h1
          ref={headingsRef}
          className="animated-hero-headings"
          style={getAssetStyle(headingsSettings.style)}
        >
          {headings.map((heading, index) => (
            <span
              key={index}
              className="animated-hero-heading"
              style={getAssetStyle(heading.style)}
            >
              {heading.title}
            </span>
          ))}
        </h1>
      </div>
    )
  }

  const renderFooter = () => (
    <div className="animated-hero-footer">
      <Container size="extended">
        <div className="animated-hero-footer-social">
          <SocialMedia />
        </div>
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      </Container>
    </div>
  )

  return (
    <div
      className={functions.classnames(
        'animated-hero',
        hasLoaded() && 'animated-hero--loaded',
      )}
    >
      {renderBackdrop()}
      {renderAssets()}
      {renderHeadings()}
      {renderFooter()}
    </div>
  )
}

export default AnimatedHero
